import { useMemo } from "react";
import { TextStyle, ViewStyle } from "react-native";
import { useTheme } from "./ThemeProvider";
import type { Theme } from "./themes";

/**
 * Zentrale Style-Definitionen, abgeleitet aus dem aktiven Theme.
 *
 * Nutzung:
 *   const s = useStyles();
 *   <View style={s.card}>
 *   <Text style={s.textH2}>
 */
export function createStyles(theme: Theme) {
  /* ─── Container ─────────────────────────────────────────── */

  const card: ViewStyle = {
    backgroundColor: theme.color.surface,
    borderRadius: theme.radius.lg,
    padding: theme.space.md,
    borderWidth: 1,
    borderColor: theme.color.border,
  };

  const cardBordered: ViewStyle = {
    backgroundColor: theme.color.surface,
    borderRadius: theme.radius.md,
    padding: theme.space.md,
    borderWidth: 1,
    borderColor: theme.color.border,
  };

  const cardSelected: ViewStyle = {
    backgroundColor: theme.color.accentBg,
    borderRadius: theme.radius.md,
    padding: theme.space.md,
    borderWidth: 2,
    borderColor: theme.color.accent,
  };

  const surface: ViewStyle = {
    backgroundColor: theme.color.surface,
    borderRadius: theme.radius.lg,
    padding: theme.space.md,
  };

  const surfaceSection: ViewStyle = {
    backgroundColor: theme.color.surface,
    borderRadius: theme.radius.lg,
    borderWidth: 1,
    borderColor: theme.color.border,
    overflow: "hidden",
  };

  /* ─── Text ──────────────────────────────────────────────── */

  const textTitle: TextStyle = {
    color: theme.color.text,
    fontSize: theme.fontSize.title,
    fontWeight: "800",
    letterSpacing: 0.1,
  };

  const textH2: TextStyle = {
    color: theme.color.text,
    fontSize: theme.fontSize.h2,
    fontWeight: "700",
    letterSpacing: 0.1,
  };

  const textH3: TextStyle = {
    color: theme.color.text,
    fontSize: theme.fontSize.h3,
    fontWeight: "700",
  };

  const textBody: TextStyle = {
    color: theme.color.text,
    fontSize: theme.fontSize.body,
  };

  const textBodyBold: TextStyle = {
    color: theme.color.text,
    fontSize: theme.fontSize.body,
    fontWeight: "700",
  };

  const textSmall: TextStyle = {
    color: theme.color.muted,
    fontSize: theme.fontSize.small,
  };

  const textTiny: TextStyle = {
    color: theme.color.muted,
    fontSize: theme.fontSize.tiny,
  };

  const textLabel: TextStyle = {
    color: theme.color.muted,
    fontSize: theme.fontSize.small,
    fontWeight: "600",
  };

  /* ─── Inputs ────────────────────────────────────────────── */

  const input: TextStyle = {
    borderWidth: 1,
    borderColor: theme.color.border,
    backgroundColor: theme.color.surface,
    borderRadius: theme.radius.md,
    paddingHorizontal: theme.space.sm,
    paddingVertical: theme.space.sm,
    color: theme.color.text,
    fontSize: theme.fontSize.body,
  };

  const inputReadonly: TextStyle = {
    ...input,
    backgroundColor: theme.color.surfaceStrong,
    opacity: 0.7,
  };

  const inputMultiline: TextStyle = {
    ...input,
    minHeight: 80,
    textAlignVertical: "top",
  };

  /* ─── Buttons ───────────────────────────────────────────── */

  const btnPrimary: ViewStyle = {
    backgroundColor: theme.color.buttonBg,
    borderRadius: theme.radius.md,
    paddingVertical: theme.space.sm,
    alignItems: "center",
  };

  const btnPrimaryText: TextStyle = {
    color: theme.color.buttonText,
    fontWeight: "700",
  };

  const btnSecondary: ViewStyle = {
    backgroundColor: theme.color.bg,
    borderRadius: theme.radius.md,
    paddingVertical: theme.space.sm,
    alignItems: "center",
  };

  const btnSecondaryText: TextStyle = {
    color: theme.color.text,
    fontWeight: "600",
  };

  const btnDanger: ViewStyle = {
    backgroundColor: theme.color.dangerBg,
    borderRadius: theme.radius.md,
    paddingVertical: theme.space.sm,
    alignItems: "center",
  };

  const btnOutline: ViewStyle = {
    borderWidth: 1,
    borderColor: theme.color.accent,
    borderRadius: theme.radius.sm,
    paddingVertical: theme.space.xs,
    paddingHorizontal: theme.space.sm,
  };

  const btnOutlineText: TextStyle = {
    color: theme.color.accent,
    fontSize: theme.fontSize.small,
    fontWeight: "600",
  };

  const btnDisabled: ViewStyle = {
    backgroundColor: theme.color.surfaceStrong,
    borderRadius: theme.radius.md,
    paddingVertical: theme.space.sm,
    alignItems: "center",
  };

  /* ─── Pills ─────────────────────────────────────────────── */

  const pillBase: ViewStyle = {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    paddingVertical: theme.space.xs,
    paddingHorizontal: theme.space.sm,
    borderRadius: theme.radius.full,
    borderWidth: 1,
  };

  const pillActive: ViewStyle = {
    ...pillBase,
    backgroundColor: theme.color.buttonBg,
    borderColor: theme.color.buttonBg,
  };

  const pillInactive: ViewStyle = {
    ...pillBase,
    backgroundColor: "transparent",
    borderColor: theme.color.border,
  };

  const pillDisabled: ViewStyle = {
    ...pillBase,
    backgroundColor: theme.color.surfaceStrong,
    borderColor: theme.color.border,
    opacity: 0.5,
  };

  /* ─── Icon-Hintergründe ─────────────────────────────────── */

  const iconBg: ViewStyle = {
    width: 32,
    height: 32,
    borderRadius: theme.radius.md,
    backgroundColor: theme.color.bg,
    justifyContent: "center",
    alignItems: "center",
  };

  const iconBgAccent: ViewStyle = {
    width: 36,
    height: 36,
    borderRadius: theme.radius.md,
    backgroundColor: theme.color.accentBg,
    justifyContent: "center",
    alignItems: "center",
  };

  const iconBgSmall: ViewStyle = {
    width: 28,
    height: 28,
    borderRadius: theme.radius.sm,
    backgroundColor: theme.color.bgStrong,
    justifyContent: "center",
    alignItems: "center",
  };

  /* ─── Badges ────────────────────────────────────────────── */

  const badge: ViewStyle = {
    minWidth: 20,
    height: 20,
    borderRadius: 10,
    backgroundColor: theme.color.badgeBg,
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: 5,
  };

  const badgeText: TextStyle = {
    color: theme.color.badgeText,
    fontSize: theme.fontSize.tiny,
    fontWeight: "800",
  };

  const badgeMuted: ViewStyle = {
    ...badge,
    backgroundColor: theme.color.badgeMutedBg,
  };

  const badgeMutedText: TextStyle = {
    ...badgeText,
    color: theme.color.badgeMutedText,
  };

  /* ─── Alerts ────────────────────────────────────────────── */

  const alertDanger: ViewStyle = {
    padding: theme.space.md,
    borderRadius: theme.radius.md,
    backgroundColor: theme.color.dangerBg,
    borderWidth: 1,
    borderColor: theme.color.danger,
  };

  const alertWarning: ViewStyle = {
    paddingVertical: theme.space.sm,
    paddingHorizontal: theme.space.md,
    backgroundColor: theme.color.warningBg,
    borderBottomWidth: 1,
    borderBottomColor: theme.color.warning,
  };

  const alertWarningText: TextStyle = {
    color: theme.color.warningText,
    fontWeight: "700",
    fontSize: theme.fontSize.small,
  };

  const alertSuccess: ViewStyle = {
    padding: theme.space.md,
    borderRadius: theme.radius.md,
    backgroundColor: theme.color.successBg,
    borderWidth: 1,
    borderColor: theme.color.success,
  };

  const alertSuccessText: TextStyle = {
    color: theme.color.success,
    fontSize: theme.fontSize.small,
  };

  const alertInfo: ViewStyle = {
    padding: theme.space.md,
    borderRadius: theme.radius.md,
    backgroundColor: theme.color.infoBg,
    borderWidth: 1,
    borderColor: theme.color.info,
  };

  const alertInfoText: TextStyle = {
    color: theme.color.info,
    fontSize: theme.fontSize.small,
  };

  /* ─── Divider ───────────────────────────────────────────── */

  const dividerBottom: ViewStyle = {
    borderBottomWidth: 1,
    borderBottomColor: theme.color.border,
  };

  const dividerTop: ViewStyle = {
    borderTopWidth: 1,
    borderTopColor: theme.color.border,
  };

  /* ─── Layout ────────────────────────────────────────────── */

  const row: ViewStyle = {
    flexDirection: "row",
    alignItems: "center",
  };

  const rowSpaced: ViewStyle = {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  };

  const rowGap: ViewStyle = {
    flexDirection: "row",
    alignItems: "center",
    gap: theme.space.sm,
  };

  const center: ViewStyle = {
    justifyContent: "center",
    alignItems: "center",
  };

  /* ─── Drawer ────────────────────────────────────────────── */

  const drawerBg: ViewStyle = {
    flexGrow: 1,
    backgroundColor: theme.color.surface,
  };

  const drawerItem: ViewStyle = {
    flexDirection: "row",
    alignItems: "center",
    gap: theme.space.md,
    paddingVertical: theme.space.sm,
    paddingHorizontal: theme.space.md,
    borderRadius: theme.radius.sm,
  };

  const drawerItemActive: ViewStyle = {
    ...drawerItem,
    backgroundColor: theme.color.surfaceStrong,
  };

  const drawerSection: TextStyle = {
    marginTop: theme.space.lg,
    marginBottom: theme.space.sm,
    color: theme.color.muted,
    fontSize: theme.fontSize.small,
    letterSpacing: 1,
    fontWeight: "700",
  };

  /* ─── Screen ────────────────────────────────────────────── */

  const screenBg: ViewStyle = {
    flex: 1,
    backgroundColor: theme.color.bg,
  };

  const screenPadding: ViewStyle = {
    padding: theme.space.xl,
  };

  /* ─── Katalog / Controls (controls.tsx) ─────────────────── */

  const backdrop: ViewStyle = {
    position: "absolute",
    top: -9999,
    left: -9999,
    right: -9999,
    bottom: -9999,
  };

  const sectionContainer: ViewStyle = {
    backgroundColor: theme.color.surface,
    borderRadius: theme.radius.lg,
    padding: theme.space.lg,
  };

  const sectionTitle: TextStyle = {
    color: theme.color.text,
    fontWeight: "700",
    fontSize: theme.fontSize.h2,
    marginBottom: theme.space.md,
    letterSpacing: 0.1,
  };

  const sectionDescription: TextStyle = {
    color: theme.color.muted,
    fontSize: theme.fontSize.small,
    marginBottom: theme.space.md,
  };

  const subLabelWrap: ViewStyle = {
    marginTop: theme.space.sm,
  };

  const subLabelText: TextStyle = {
    color: theme.color.muted,
    fontSize: theme.fontSize.tiny,
    fontWeight: "700",
    letterSpacing: 1,
  };

  const subLabelInfoBox: ViewStyle = {
    borderLeftWidth: 2,
    borderLeftColor: theme.color.border,
    paddingLeft: theme.space.sm,
    marginTop: 4,
  };

  const subLabelInfoLine: TextStyle = {
    color: theme.color.placeholder,
    fontSize: 10,
    lineHeight: 14,
  };

  const fieldLabelText: TextStyle = {
    color: theme.color.text,
    fontWeight: "600",
    marginBottom: theme.space.xs,
  };

  const pillOptionRow: ViewStyle = {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: theme.space.sm,
  };

  const pillOption: ViewStyle = {
    alignItems: "center",
    paddingVertical: theme.space.xs,
    paddingHorizontal: theme.space.sm,
    borderRadius: theme.radius.md,
    borderWidth: 1,
  };

  const pillOptionActive: ViewStyle = {
    ...pillOption,
    borderColor: theme.color.primary,
    backgroundColor: theme.color.accentBg,
  };

  const pillOptionInactive: ViewStyle = {
    ...pillOption,
    borderColor: theme.color.border,
    backgroundColor: theme.color.bgStrong,
  };

  const pillOptionText: TextStyle = {
    color: theme.color.text,
    fontWeight: "600",
  };

  const pillOptionTextActive: TextStyle = {
    ...pillOptionText,
    fontWeight: "800",
  };

  const dateTrigger: ViewStyle = {
    flexDirection: "row",
    alignItems: "center",
    borderWidth: 1,
    borderColor: theme.color.border,
    borderRadius: theme.radius.md,
    paddingHorizontal: theme.space.sm,
    paddingVertical: theme.space.sm,
  };

  const dateTriggerText: TextStyle = {
    flex: 1,
    color: theme.color.text,
    fontSize: theme.fontSize.body,
  };

  const dateTriggerTextPlaceholder: TextStyle = {
    ...dateTriggerText,
    color: theme.color.muted,
  };

  const dateCalendarWrap: ViewStyle = {
    position: "relative",
    zIndex: 1,
    alignSelf: "flex-start",
    width: 260,
    borderWidth: 1,
    borderColor: theme.color.border,
    backgroundColor: theme.color.surface,
    borderRadius: theme.radius.md,
    padding: theme.space.sm,
  };

  const dateMonthRow: ViewStyle = {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: theme.space.sm,
  };

  const dateWeekday: TextStyle = {
    flex: 1,
    textAlign: "center",
    color: theme.color.muted,
    fontSize: theme.fontSize.tiny,
    fontWeight: "700",
    marginBottom: 4,
  };

  const dateDayCell: ViewStyle = {
    width: "14.28%",
    height: 32,
    alignItems: "center",
    justifyContent: "center",
  };

  const dateDayInner: ViewStyle = {
    width: 28,
    height: 28,
    borderRadius: 14,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "transparent",
  };

  const dateDayInnerSelected: ViewStyle = {
    ...dateDayInner,
    backgroundColor: theme.color.primary,
  };

  const dateDayInnerToday: ViewStyle = {
    ...dateDayInner,
    borderWidth: 1,
    borderColor: theme.color.accent,
  };

  const dateDayText: TextStyle = {
    color: theme.color.text,
    fontSize: theme.fontSize.small,
    fontWeight: "400",
  };

  const dateDayTextSelected: TextStyle = {
    color: theme.color.buttonText,
    fontSize: theme.fontSize.small,
    fontWeight: "800",
  };

  const filterInputRow: ViewStyle = {
    flexDirection: "row",
    alignItems: "center",
    borderWidth: 1,
    borderColor: theme.color.border,
    backgroundColor: theme.color.surface,
    borderRadius: theme.radius.md,
    paddingHorizontal: theme.space.sm,
  };

  const filterDropdownBox: ViewStyle = {
    borderWidth: 1,
    borderColor: theme.color.border,
    backgroundColor: theme.color.surface,
    borderRadius: theme.radius.md,
    overflow: "hidden",
    maxHeight: 220,
  };

  const filterReadonlyBox: ViewStyle = {
    borderWidth: 1,
    borderColor: theme.color.border,
    backgroundColor: theme.color.surfaceStrong,
    borderRadius: theme.radius.md,
    paddingHorizontal: theme.space.sm,
    paddingVertical: theme.space.sm,
    opacity: 0.7,
  };

  const catalogHeaderTitle: TextStyle = {
    color: theme.color.text,
    fontSize: theme.fontSize.title,
    fontWeight: "800",
    letterSpacing: 0.1,
  };

  const catalogHeaderSubtitle: TextStyle = {
    color: theme.color.muted,
    fontSize: theme.fontSize.body,
    marginTop: 4,
  };

  const catalogToggle: ViewStyle = {
    flexDirection: "row",
    alignItems: "center",
    alignSelf: "flex-start",
    gap: theme.space.sm,
    paddingVertical: theme.space.sm,
    paddingHorizontal: theme.space.md,
    borderRadius: theme.radius.md,
    borderWidth: 1,
  };

  const catalogToggleActive: ViewStyle = {
    ...catalogToggle,
    borderColor: theme.color.primary,
    backgroundColor: theme.color.accentBg,
  };

  const catalogToggleInactive: ViewStyle = {
    ...catalogToggle,
    borderColor: theme.color.border,
    backgroundColor: theme.color.bgStrong,
  };

  const catalogToggleText: TextStyle = {
    color: theme.color.text,
    fontWeight: "700",
    fontSize: theme.fontSize.body,
  };

  const catalogPopupWrap: ViewStyle = {
    position: "relative",
    zIndex: 50,
    overflow: "visible",
  };

  const catalogColorPreview: ViewStyle = {
    width: 14,
    height: 14,
    borderRadius: 3,
  };

  return {
    // Container
    card,
    cardBordered,
    cardSelected,
    surface,
    surfaceSection,

    // Text
    textTitle,
    textH2,
    textH3,
    textBody,
    textBodyBold,
    textSmall,
    textTiny,
    textLabel,

    // Input
    input,
    inputReadonly,
    inputMultiline,

    // Button
    btnPrimary,
    btnPrimaryText,
    btnSecondary,
    btnSecondaryText,
    btnDanger,
    btnOutline,
    btnOutlineText,
    btnDisabled,

    // Pill
    pillActive,
    pillInactive,
    pillDisabled,

    // Icon
    iconBg,
    iconBgAccent,
    iconBgSmall,

    // Badge
    badge,
    badgeText,
    badgeMuted,
    badgeMutedText,

    // Alert
    alertDanger,
    alertWarning,
    alertWarningText,
    alertSuccess,
    alertSuccessText,
    alertInfo,
    alertInfoText,

    // Divider
    dividerBottom,
    dividerTop,

    // Layout
    row,
    rowSpaced,
    rowGap,
    center,

    // Drawer
    drawerBg,
    drawerItem,
    drawerItemActive,
    drawerSection,

    // Screen
    screenBg,
    screenPadding,

    // Catalog / Controls (controls.tsx)
    backdrop,
    sectionContainer,
    sectionTitle,
    sectionDescription,
    subLabelWrap,
    subLabelText,
    subLabelInfoBox,
    subLabelInfoLine,
    fieldLabelText,
    pillOptionRow,
    pillOptionActive,
    pillOptionInactive,
    pillOptionText,
    pillOptionTextActive,
    dateTrigger,
    dateTriggerText,
    dateTriggerTextPlaceholder,
    dateCalendarWrap,
    dateMonthRow,
    dateWeekday,
    dateDayCell,
    dateDayInner,
    dateDayInnerSelected,
    dateDayInnerToday,
    dateDayText,
    dateDayTextSelected,
    filterInputRow,
    filterDropdownBox,
    filterReadonlyBox,
    catalogHeaderTitle,
    catalogHeaderSubtitle,
    catalogToggle,
    catalogToggleActive,
    catalogToggleInactive,
    catalogToggleText,
    catalogPopupWrap,
    catalogColorPreview,
  };
}

export type AppStyles = ReturnType<typeof createStyles>;

/** Hook: gibt memoisierte Styles zurück, die sich bei Theme-Wechsel aktualisieren. */
export function useStyles() {
  const { theme } = useTheme();
  return useMemo(() => createStyles(theme), [theme]);
}

/**
 * Standard-Styles für Text-Eingaben (einheitlicher Look).
 * Migriert aus FormField.tsx – unterstützt editable/multiline Varianten.
 */
export function useInputStyle(options?: {
  editable?: boolean;
  multiline?: boolean;
}) {
  const { theme } = useTheme();
  const editable = options?.editable ?? true;
  const multiline = options?.multiline ?? false;

  return {
    borderWidth: 1,
    borderColor: theme.color.border,
    backgroundColor: editable ? theme.color.surface : theme.color.surfaceStrong,
    borderRadius: theme.radius.md,
    paddingHorizontal: theme.space.sm,
    paddingVertical: theme.space.sm,
    color: theme.color.text,
    fontSize: theme.fontSize.body,
    opacity: editable ? 1 : 0.7,
    ...(multiline && { minHeight: 80, textAlignVertical: "top" as const }),
  };
}
